import type { QuestionOption, TableFormColumn } from '@common/types/templateFormFields';
import { parseMultiSelectValue } from '@common/utils/intakeFieldFormatting';

export interface TableFormRow {
    [key: string]: string | string[] | null | undefined;
}

export type MultiSelectOption = QuestionOption;

export function stringifyMultiSelectValue(selected: string[]): string {
    return JSON.stringify(selected);
}

export function sortMultiSelectByOptions(
    selected: string[],
    options?: MultiSelectOption[],
): string[] {
    if (!options?.length) {
        return selected;
    }

    const optionOrder = new Map(
        options.map((option, index) => [String(option.optionValue), index]),
    );
    return [...selected].sort((a, b) => {
        const aIndex = optionOrder.get(a) ?? Number.MAX_SAFE_INTEGER;
        const bIndex = optionOrder.get(b) ?? Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
    });
}

export function createEmptyTableRow(columns: TableFormColumn[]): TableFormRow {
    const row: TableFormRow = {};
    for (const col of columns) {
        if (col.type === 'upload') {
            row[col.key] = [];
        } else if (col.type === 'multiselect') {
            row[col.key] = stringifyMultiSelectValue([]);
        } else {
            row[col.key] = '';
        }
    }
    return row;
}

export function getFixedTableRowCount(rowCount: number | null | undefined): number | undefined {
    if (rowCount == null || !Number.isInteger(rowCount) || rowCount < 1) {
        return undefined;
    }
    return rowCount;
}

export function parseTableRows(
    value: string | undefined,
    columns: TableFormColumn[],
    rowCount?: number | null,
): TableFormRow[] {
    const fixedRowCount = getFixedTableRowCount(rowCount);
    let rows: TableFormRow[] = [];

    if (value) {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                rows = parsed;
            }
        } catch {
            rows = [];
        }
    }

    if (fixedRowCount !== undefined) {
        const normalized = rows.slice(0, fixedRowCount);
        while (normalized.length < fixedRowCount) {
            normalized.push(createEmptyTableRow(columns));
        }
        return normalized;
    }

    if (rows.length === 0) {
        return [createEmptyTableRow(columns)];
    }
    return rows;
}

export function isEmptyTableCellValue(
    cellValue: string | string[] | null | undefined,
    columnType: TableFormColumn['type'],
): boolean {
    if (columnType === 'upload') {
        return !Array.isArray(cellValue) || cellValue.length === 0;
    }
    if (columnType === 'multiselect') {
        return (
            parseMultiSelectValue(cellValue as string | string[] | null | undefined).length === 0
        );
    }
    if (Array.isArray(cellValue)) {
        return cellValue.length === 0;
    }
    return !cellValue || String(cellValue).trim() === '';
}

export function isTableRowComplete(row: TableFormRow, columns: TableFormColumn[]): boolean {
    return columns.every((column) => !isEmptyTableCellValue(row[column.key], column.type));
}

export function isRequiredTableValueEmpty(
    value: string | null | undefined,
    columns: TableFormColumn[] | undefined,
    rowCount?: number | null,
): boolean {
    if (!columns?.length) {
        return true;
    }

    const rows = parseTableRows(value ?? undefined, columns, rowCount);
    return rows.some((row) => !isTableRowComplete(row, columns));
}
